import React, { useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import { Button, InputAdornment, TextField, Chip, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

const BookingAddTable = ({ bookingList, onEdit, onDelete }) => {
  const [searchValue, setSearchValue] = useState("");

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  // Convert time to 12-hour format
  function convertTo12HourFormat(timeString) {
    if (!timeString) return "N/A";
    let [hours, minutes] = timeString.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  }

  function formatDate(dateString) {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day}-${month}-${year}, ${hours}:${minutes} ${ampm}`;
  }


  const rows = bookingList?.map((booking) => ({
    id: booking.id,
    service_name: booking.service,
    phone: booking.phone,
    customer_name: booking.name,
    customer_address: booking.address,
    date: booking.booked_date,
    worker_name: booking.worker_name,
    time: convertTo12HourFormat(booking.booked_time),
    created_at: formatDate(booking.created_at),
    status: booking.status,
    booking_price: booking.booking_price,
    miscellaneous_price: booking.miscellaneous_price,
    payment_method: booking.payment_method,
    payable_amount: booking.payable_amount,
    service_charge: booking.service_charge,
    is_booked: booking.is_booked,
    is_paid: booking.is_paid,
    latitude: booking.latitude,
    longitude: booking.longitude,
    total_price: booking.total_price,
    miscellaneous_price_comment: booking.miscellaneous_price_comment,
    service_charge_comment: booking.service_charge_comment,
    booked_method: booking.booked_method,
    rating: booking.rating,
    service_id: booking.service_id,
    worker: booking.worker,
    booked_time: booking.booked_time
  }));

  const filteredBooking = rows?.filter((booking) =>
    Object.entries(booking).some(([key, value]) => {
      if (Array.isArray(value)) {
        return value.some((item) =>
          item.toString().toLowerCase().includes(searchValue.toLowerCase())
        );
      }

      if (typeof value === "boolean") {
        const stringValue =
          key === "is_paid"
            ? value
              ? "Paid"
              : "Pending"
            : key === "is_booked"
            ? value
              ? "Booked"
              : "Available"
            : "";

        return stringValue.toLowerCase().includes(searchValue.toLowerCase());
      }

      if (key === "id") {
        return value.toString().toLowerCase().includes(searchValue.toLowerCase());
      }

      return (
        value &&
        value.toString().toLowerCase().includes(searchValue.toLowerCase())
      );
    })
  );

  const displayBooking = searchValue ? filteredBooking : rows;

  const bookingWithIndex = displayBooking?.map((booking, index) => ({
    ...booking,
    displayIndex: index + 1,
  }));

  const editableStatuses = ["Waiting", "Accepted", "Estimated"];


  const columns = [
    { field: "displayIndex", headerName: "No", width: 90 },
    { field: "id", headerName: "ID", width: 90 },
    { field: "service_name", headerName: "Service Name", width: 150 },
    { field: "customer_name", headerName: "Customer Name", width: 150 },
    { field: "phone", headerName: "Customer Number", width: 150 },
    { field: "customer_address", headerName: "Customer Address", width: 170 },
    { field: "date", headerName: "Date", width: 150 },
    { field: "time", headerName: "Time", width: 110 },
    { field: "created_at", headerName: "Created at", width: 180 },
    { field: "worker_name", headerName: "Worker", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        let chipColor;

        switch (params.value) {
          case "Waiting":
            chipColor = "warning";
            break;
          case "Accepted":
            chipColor = "success";
            break;
          case "Cancelled":
            chipColor = "error";
            break;
          case "Estimated":
            chipColor = "primary";
            break;
          case "Completed":
            chipColor = "secondary";
            break;
          default:
            chipColor = "default";
        }

        return (
          <Chip
            label={params.value}
            color={chipColor}
            style={{
              color: "white",
              minWidth: "100px",
            }}
          />
        );
      },
    },
    {
      field: "is_booked",
      headerName: "Booking Status",
      width: 150,
      renderCell: (params) => {
        const isBooked = params.value;
        const tooltipTitle = isBooked ? params.row.booked_method || "N/A" : "";

        return (
          <Tooltip
            title={tooltipTitle}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#333",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "8px",
                  borderRadius: "6px",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                },
              },
            }}
          >
            <span
              style={{
                color: isBooked ? "green" : "red",
                fontWeight: "bold",
                minWidth: "100px",
                display: "inline-block",
                textAlign: "center",
                cursor: isBooked ? "pointer" : "default",
              }}
            >
              {isBooked ? "₹100 Taken" : "-"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "is_paid",
      headerName: "Final Payment Status",
      width: 150,
      renderCell: (params) => (
        <span
          style={{ color: params.value ? "green" : "red", fontWeight: "bold" }}
        >
          {params.value ? "Paid" : "Pending"}
        </span>
      ),
    },
    {
      field: "rating",
      headerName: "Rating",
      width: 150,
      renderCell: (params) => {
        const rating = params.value?.rating || "No Rating";
        const comment = params.value?.comment;

        const ratingColors = {
          Excellent: "success",
          Good: "primary",
          Satisfied: "info",
          "Not Satisfied": "warning",
          Poor: "error",
          "No Rating": "default",
        };

        return (
          <Tooltip
            title={comment ? comment : ""}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#333",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "8px",
                  borderRadius: "6px",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                },
              },
            }}
          >
            <Chip
              label={rating}
              color={ratingColors[rating] || "default"}
              sx={{ cursor: comment ? "pointer" : "default" }}
            />
          </Tooltip>
        );
      },
    },
    { field: "payment_method", headerName: "Payment Method", width: 130 },
    { field: "booking_price", headerName: "Booking Price", width: 130 },
    {
      field: "miscellaneous_price",
      headerName: "Miscellaneous Price",
      width: 150,
    },
    {
      field: "service_charge",
      headerName: "Service Charge",
      width: 150,
    },
    {
      field: "total_price",
      headerName: "Total Price",
      width: 150,
    },
    {
      field: "payable_amount",
      headerName: "Payable Amount",
      width: 150,
    },
    {
      field: "miscellaneous_price_comment",
      headerName: "Miscellaneous Price Comment",
      width: 200,
    },
    {
      field: "service_charge_comment",
      headerName: "Service Charge Comment",
      width: 200,
    },
    {
      field: "location",
      headerName: "Location",
      width: 200,
      renderCell: (params) => {
        const { latitude, longitude } = params.row;
        if (!latitude || !longitude) return "N/A";

        const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

        return (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "blue", textDecoration: "underline" }}
          >
            {latitude}, {longitude}
          </a>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <div className="action-btn">
          {editableStatuses.includes(params.row.status) && (
            <Button
              variant="outlined"
              color="primary"
              style={{ marginRight: "5px" }}
              onClick={() => {
                onEdit(params);
              }}
            >
              <EditIcon />
            </Button>
          )}
          <Button
            variant="outlined"
            style={{ marginRight: "5px" }}
            color="error"
            onClick={() => onDelete(params.row)}
          >
            <DeleteIcon />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="search-container d-flex justify-content-center my-2">
        <TextField
          id="search"
          name="search"
          className="search-field"
          placeholder="Search here..."
          value={searchValue}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: "transparent" },
              "&.Mui-focused fieldset": { borderColor: "transparent" },
            },
          }}
        />
      </div>
      <Box
        sx={{
          flexGrow: 1,
          maxWidth: { xs: 650, sm: 682, lg: 1350 },
          bgcolor: "background.paper",
        }}
      >
        <DataGrid
          rows={bookingWithIndex || []}
          columns={columns}
          disableSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
          }}
        />
      </Box>
    </>
  );
};

export default BookingAddTable;